import { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import PropTypes from "prop-types";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { usePermissions } from "context/PermissionsContext";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftButton from "components/SoftButton";
import AlertMessage from "components/UI/AlertMessage";
import Unauthorized from "components/UI/Unauthorized";
import useAPI from "hooks/useAPI";
import useAlert from "hooks/useAlert";
import SubJobs from "./subjobs";
import colors from "assets/theme/base/colors";

// ─── Step icon ────────────────────────────────────────────────────────────────

const RtlStepIcon = (props) => {
  const { active, completed, className } = props;
  return (
    <div className={className} dir="ltr">
      {completed ? (
        <CheckIcon style={{ color: "green" }} />
      ) : (
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: active ? "#1976d2" : "#ccc",
          }}
        />
      )}
    </div>
  );
};

RtlStepIcon.propTypes = {
  active: PropTypes.bool,
  completed: PropTypes.bool,
  className: PropTypes.string,
};

// ─── Status → step mapping ────────────────────────────────────────────────────

const statusToStep = {
  pending: 0,
  processing: 1,
  running: 1,
  completed: 2,
};

const locationToStep = {
  GWH: 0,
  GWM: 1,
  BWH: 2,
};

// ─── Main component ───────────────────────────────────────────────────────────

const JobDetails = () => {
  const { warning } = colors;
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const { errorState, showAlert } = useAlert();
  const { api } = useAPI();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { t } = useTranslation();

  if (!hasPermission("can_view_gbwm")) {
    return <Unauthorized />;
  }

  const jobStatus = job?.status || "pending";
  const currentStep = statusToStep[jobStatus] ?? 0;

  const steps = [
    t("lang.job_request_received"),
    t("lang.job_request_sent"),
    t("lang.checking_job_status"),
  ];

  const getStepLabel = (index) => {
    switch (index) {
      case 0:
        return t("lang.job_request_received_from", { service: job?.service });
      case 1:
        return t("lang.job_request_sent_to_gwm");
      case 2:
        return t("lang.awaiting_confirmation_from_bwh");
      default:
        return "";
    }
  };

  const fetchJobById = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/get-job/${id}/`);
      setJob(response.data);
    } catch (error) {
      showAlert("error", error?.response?.data?.error || t("lang.failed_to_fetch_job_details"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobById();
  }, [id]);

  const pagesList = [{ name: t("lang.job_details"), route: "" }];

  return (
    <DashboardLayout>
      <DashboardNavbar pagesList={pagesList} />

      {/* Progress bar while loading */}
      {loading && (
        <Box mb={2}>
          <LinearProgress sx={{ borderRadius: 3 }} />
        </Box>
      )}

      {/* Stepper card */}
      {!loading && job && (
        <>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2.5,
              overflow: "hidden",
              mb: 2,
            }}
          >
            {/* Header */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              px={3}
              py={2}
              sx={{ borderBottom: "1px solid", borderColor: "divider" }}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight={700} lineHeight={1}>
                  {t("lang.job_details") || "Job Details"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  #{id}
                </Typography>
              </Box>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {t("lang.status") || "Status"}:
                </Typography>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    background:
                      jobStatus === "completed"
                        ? "rgba(34,197,94,0.10)"
                        : jobStatus === "failed"
                        ? "rgba(239,68,68,0.10)"
                        : jobStatus === "running"
                        ? "rgba(59,130,246,0.10)"
                        : "rgba(245,158,11,0.10)",
                    color:
                      jobStatus === "completed"
                        ? "#22c55e"
                        : jobStatus === "failed"
                        ? "#ef4444"
                        : jobStatus === "running"
                        ? "#3b82f6"
                        : "#f59e0b",
                  }}
                >
                  {jobStatus.charAt(0).toUpperCase() + jobStatus.slice(1)}
                </Typography>
              </Stack>
            </Stack>

            {/* Stepper */}
            <Box px={3} py={3}>
              <Stepper activeStep={currentStep} alternativeLabel>
                {steps.map((label, index) => {
                  let optionalLabel = "";
                  let color = "gray";
                  let isCompleted = false;
                  let isCurrent = false;
                  let isFailed = false;

                  if (jobStatus === "failed") {
                    const failedStepIndex = locationToStep[job?.current_location] ?? 0;
                    if (index < failedStepIndex) {
                      color = "green";
                      isCompleted = true;
                      optionalLabel = getStepLabel(index);
                    } else if (index === failedStepIndex) {
                      isFailed = true;
                      optionalLabel = getStepLabel(index);
                      color = "error";
                    } else {
                      optionalLabel = getStepLabel(index);
                    }
                  } else {
                    isCompleted =
                      index < currentStep || (index === currentStep && jobStatus === "completed");
                    isCurrent = index === currentStep && !isCompleted;
                    const isWaiting = index > currentStep;

                    if (isCompleted) {
                      color = "green";
                      optionalLabel = getStepLabel(index);
                    } else if (isCurrent) {
                      color = warning.main;
                      optionalLabel = getStepLabel(index);
                    } else if (isWaiting) {
                      optionalLabel = getStepLabel(index);
                    }
                  }

                  return (
                    <Step key={label} completed={isCompleted}>
                      <StepLabel
                        error={isFailed}
                        optional={
                          <Typography
                            variant="caption"
                            color={color}
                            sx={{ opacity: 0.8, fontSize: "0.75rem" }}
                          >
                            {optionalLabel}
                          </Typography>
                        }
                        sx={{
                          ...(isCompleted && {
                            "& .MuiStepLabel-label": { color: "green !important" },
                            "& .MuiStepIcon-root.Mui-completed": { color: "green" },
                          }),
                          ...(isCurrent && {
                            "& .MuiStepLabel-label": { color: warning.main + " !important" },
                            "& .MuiStepIcon-root": { color: warning.main },
                            "& .MuiStepIcon-root.Mui-active": { color: warning.main },
                          }),
                        }}
                      >
                        {label}
                      </StepLabel>
                    </Step>
                  );
                })}
              </Stepper>
            </Box>
          </Paper>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2.5,
              overflow: "hidden",
              mb: 2,
            }}
          >
            <Box p={2}>
              <SubJobs id={id} />
            </Box>
          </Paper>
        </>
      )}

      {/* Back button */}
      <Box>
        <Divider sx={{ mb: 2 }} />
        <SoftButton
          variant="contained"
          startIcon={<ArrowBackIcon />}
          color="dark"
          onClick={() => navigate("/jobs")}
        >
          {t("lang.actions.back")}
        </SoftButton>
      </Box>

      <AlertMessage {...errorState} />
    </DashboardLayout>
  );
};

export default JobDetails;
